import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { openLink, type HeaderNavGroup } from './DynamicHeaderFooter';
import styles from './MobileNavMenu.module.css';

/**
 * Mobile menu for the tenant header links (headerConfigForMobile): a ☰ button that opens a
 * side panel listing every Link and Dropdown item. Flutter draws the same links inline
 * (DynamicHeader); on a phone they read better as a full-height list with large tap targets.
 * Links open in a new tab, as Flutter's launchUrl does on web.
 */
export function MobileNavMenu({ groups, logoUrl, brandName }: {
  groups: HeaderNavGroup[];
  logoUrl?: string;
  brandName: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // A single labelled dropdown ("Menu") names the button; its heading would just repeat it
  const soleLabel = groups.length === 1 ? groups[0].label : undefined;
  const menuLabel = soleLabel || t('Menu');

  useEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKeyDown);
      trigger?.focus();
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(true)}
        aria-label={menuLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
          <path d="M3.5 6h15M3.5 11h15M3.5 16h15" />
        </svg>
      </button>

      {open && createPortal(
        <div className={styles.root}>
          <div className={styles.backdrop} onClick={() => setOpen(false)} />
          <nav className={styles.panel} role="dialog" aria-modal="true" aria-label={menuLabel}>
            <div className={styles.panelHeader}>
              {logoUrl ? <img className={styles.logo} src={logoUrl} alt={brandName} /> : <span />}
              <button
                ref={closeRef}
                type="button"
                className={styles.close}
                onClick={() => setOpen(false)}
                aria-label={t('Close')}
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                  <path d="M5 5l10 10M15 5L5 15" />
                </svg>
              </button>
            </div>

            <div className={styles.body}>
              {groups.map((group, i) => (
                <section key={i} className={styles.group}>
                  {group.label && !soleLabel && <h2 className={styles.groupLabel}>{group.label}</h2>}
                  <ul className={styles.list}>
                    {group.items.map((item, j) => (
                      <li key={j}>
                        <button
                          type="button"
                          className={styles.item}
                          onClick={() => {
                            setOpen(false);
                            openLink(item.url);
                          }}
                        >
                          <span className={styles.itemLabel}>{item.label}</span>
                          <svg className={styles.itemIcon} width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M9.5 2.5h4v4M13.5 2.5 7.5 8.5M12 9.5v3a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h3" />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          </nav>
        </div>,
        document.body,
      )}
    </>
  );
}
